import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { getIn, useFormikContext } from "formik";
import { withNamespace } from "components/Form/FormikControls";
import { Column } from "components/Layout";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { ContactColumnDefinitions } from "components/Business/BusinessForm/EmailRecipients/GridConfigurations";
import { CollapseTableWrapper } from "components/AgGrid/TableWrapper";
import { AgGridTables } from "constants/aggrid";
import { useSelector } from "react-redux";
import { getBusinessById } from "selectors/businesses";

interface OwnEmailRecipientsTableProps {
  businessId: string;
  primaryContactEmail: string | null;
  namespace: string;
  onEndEditing: () => void;
  onStartEditing: () => void;
  onSetPrimaryContactEmail: (email: string) => void;
}

export function OwnEmailRecipientsTable(
  props: OwnEmailRecipientsTableProps,
): React.JSX.Element {
  const {
    businessId,
    primaryContactEmail,
    namespace: ns,
    onEndEditing,
    onStartEditing,
    onSetPrimaryContactEmail,
  } = props;
  const formikContext = useFormikContext();
  const { dirty, values, setFieldValue, setTouched } = formikContext;

  const emailRecipients: EmailRecipient[] = useMemo(
    () => getIn(values, withNamespace(ns, "values")) || [],
    [values, ns],
  );

  const { businessUsers = [] } = useSelector(getBusinessById(businessId)) || {};
  const businessUserEmails = businessUsers.map(
    businessUser => businessUser.email,
  );

  // Push the some calculated fields in as data, based on the local form data, rather than trying to make context refresh/force manual redraws/etc in AgGrid.
  const tableData = useMemo(() => {
    const primaryEmailRecipient =
      primaryContactEmail &&
      emailRecipients.find(er => er.email === primaryContactEmail);
    return emailRecipients.map(emailRecipient => ({
      ...emailRecipient,
      isPrimary:
        primaryEmailRecipient && emailRecipient.id === primaryEmailRecipient.id,
      isMyNousActive: businessUserEmails.includes(emailRecipient.email),
    }));
  }, [emailRecipients, primaryContactEmail, businessUserEmails]);

  const onEditEmailRecipientIndex = useCallback(
    (emailRecipientId: number) => {
      if (emailRecipients.length === 0) return;
      // Move a copy of the selected EmailRecipient to the `editingValues` field to fill in the form
      const recipient = emailRecipients?.find(
        ({ id }: EmailRecipient) => id === emailRecipientId,
      );

      if (recipient) {
        setFieldValue(withNamespace(ns, "editingValue"), {
          ...recipient,
        });
      }

      // Clear any Formik validation from being displayed
      setTouched({});
      onStartEditing();
    },
    [emailRecipients, ns, setFieldValue, setTouched, onStartEditing],
  );

  const onRemoveEmailRecipientIndex = useCallback(
    (emailRecipientId: number) => {
      if (emailRecipients.length === 0) return;
      const newState = emailRecipients.slice();
      const index = newState.findIndex(
        ({ id }: EmailRecipient) => id === emailRecipientId,
      );
      if (index !== -1) {
        newState.splice(index, 1);
        setFieldValue(withNamespace(ns, "values"), newState, false);
        onStartEditing();
      }
    },
    [emailRecipients, ns, onStartEditing, setFieldValue],
  );

  useEffect(() => {
    // When something in the Formik context, or any of the props have changed, and it results in the form swapping from clean to dirty, or dirty to clean,
    // including all of the EmailRecipients and the new/edit form values, let the parent that is being edited
    if (dirty) {
      onStartEditing();
    } else {
      onEndEditing();
    }
  }, [dirty, onEndEditing, onStartEditing]);

  const onEditEmailRecipientIndexRef = useRef(onEditEmailRecipientIndex);
  const onRemoveEmailRecipientIndexRef = useRef(onRemoveEmailRecipientIndex);
  useEffect(() => {
    onEditEmailRecipientIndexRef.current = onEditEmailRecipientIndex;
    onRemoveEmailRecipientIndexRef.current = onRemoveEmailRecipientIndex;
  }, [emailRecipients, onEditEmailRecipientIndex, onRemoveEmailRecipientIndex]);

  const context = {
    onSetPrimaryContactEmail,
    onEditEmailRecipientIndexRef,
    onRemoveEmailRecipientIndexRef,
  };

  return (
    <Column full>
      <AgGridTable
        rowData={tableData}
        getRowId={params => params.data.id}
        showGlobalSearchFilter={false}
        context={context}
        columnDefs={ContactColumnDefinitions}
        WrapperComponent={CollapseTableWrapper}
        hasExportCsvPermissions={false}
        hideSavedViews
        tableName={AgGridTables.BUSINESS_EMAIL_RECIPIENTS}
      />
    </Column>
  );
}
